import type { FC } from "react";

import { m } from "@recobra/i18n/messages";
import type { Task } from "@recobra/schemas/task";
import { Button } from "@recobra/ui/button";
import { DialogFooter } from "@recobra/ui/dialog";

import { RequirementChecklist } from "../requirement-checklist";
import type { TaskProgressInput } from "../task-data";
import { TaskContextStatus, TaskDetailsSummary, useTaskMetadataEditor } from "../task-details";
import type { TaskRequirementContext } from "../task-details";
import { TaskBlockedFooter } from "../task-execution";
import { TaskCompletionRecord } from "../task-execution";
import { TaskExecutionSection } from "../task-execution";
import type { TaskCompletionInput, TaskExecutionPolicy } from "../task-execution";
import { TaskCancellationDetails, TaskLifecycleAction } from "../task-lifecycle";
import type { TaskCompletionBlocker } from "../task-policy";

export const TaskModalBody: FC<{
  task: Task;
  canExecute: boolean;
  completionBlocker: TaskCompletionBlocker | null;
  executionOnly: boolean;
  context: TaskRequirementContext;
  policy: TaskExecutionPolicy;
  isUpdating: boolean;
  onProgress: (input: TaskProgressInput) => void;
  onClose: () => void;
  onConfirm: (input: TaskCompletionInput) => void;
}> = ({
  task,
  canExecute,
  completionBlocker,
  executionOnly,
  context,
  policy,
  isUpdating,
  onProgress,
  onClose,
  onConfirm,
}) => {
  const editor = useTaskMetadataEditor(task, executionOnly);
  const lifecycleAction = <TaskLifecycleAction task={task} />;

  if (task.status === "done") {
    return <TaskCompletionRecord task={task} context={context} editor={editor} onClose={onClose} />;
  }

  if (canExecute) {
    return (
      <TaskExecutionSection
        task={task}
        leadingAction={lifecycleAction}
        context={context}
        policy={policy}
        editor={editor}
        onProgress={onProgress}
        onCancel={onClose}
        onConfirm={onConfirm}
        isSubmitting={isUpdating}
      />
    );
  }

  return (
    <>
      <div className="-mx-4 max-h-(--task-content-max-height) overflow-y-auto px-4">
        <div className="flex flex-col divide-y">
          <TaskDetailsSummary task={task} editor={editor} />
          <TaskContextStatus context={context} />
          {task.status === "canceled" ? <TaskCancellationDetails task={task} /> : null}
          <RequirementChecklist
            task={task}
            flow={context.taskType}
            resolutionId={task.solution ?? null}
            editor={editor}
            disabled
            handlers={{
              onSatisfy: () => undefined,
              onWaive: () => undefined,
              onNavigate: onClose,
            }}
          />
        </div>
      </div>

      {task.status === "canceled" ? (
        <DialogFooter className="sm:justify-start">
          {context.unconfigured ? null : lifecycleAction}
          <Button type="button" variant="outline" onClick={onClose}>
            {m.dialog_close()}
          </Button>
        </DialogFooter>
      ) : null}
      {completionBlocker ? (
        <TaskBlockedFooter
          leadingAction={lifecycleAction}
          blocker={completionBlocker}
          onClose={onClose}
        />
      ) : null}
    </>
  );
};
